/**
 * Naming a new tile on the floor plan — FRONTEND.md §9.223, FB-369.
 *
 * **The name is a join key.** `TableMapPage` matches orders to tiles through `byTableName`, keyed on
 * `table.name`, so two tiles sharing a name resolve to one order: the occupied table draws twice and
 * a tap on the empty duplicate opens the other's bill. That is why naming gets its own module and a
 * test, rather than an expression inside the click handler — which is where the bug lived.
 *
 * Legacy names a tile `S${tables.length + 1}` (`table-map.tsx:302`) and AGOS ported it exactly. The
 * count equals the highest number in use only until something is deleted.
 */

/** What the auto-named tiles are called. Anything else on the map was typed by the owner. */
export const TABLE_NAME_PREFIX = 'S'

const AUTO_NAME = /^S(\d+)$/

/** The number an auto-generated name holds, or `null` for a name a person chose. */
function ordinalOf(name: string): number | null {
  const match = AUTO_NAME.exec(name.trim())
  if (match === null) return null

  const ordinal = Number(match[1])
  // `S0` and `S007` are not names this ever produced, so they hold no ordinal and block nothing.
  return ordinal > 0 && `${ordinal}` === match[1] ? ordinal : null
}

/**
 * The smallest free number, so a deleted table's gap is filled before the sequence extends.
 *
 * With `S1`–`S5` and no deletions this returns 6, exactly as the count did. With `S3` deleted it
 * returns 3 where the count returned 5 — the name `S5` already on the map.
 */
export function nextTableOrdinal(tables: readonly { name: string }[]): number {
  const taken = new Set<number>()
  for (const table of tables) {
    const ordinal = ordinalOf(table.name)
    if (ordinal !== null) taken.add(ordinal)
  }

  let ordinal = 1
  while (taken.has(ordinal)) ordinal += 1
  return ordinal
}

/** The name and the position both follow the ordinal, so a re-added tile lands in the freed hole. */
export function nextTableName(tables: readonly { name: string }[]): string {
  return `${TABLE_NAME_PREFIX}${nextTableOrdinal(tables)}`
}
